import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { limit, onSnapshot, orderBy, query, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { activityFeedColl, petDoc, streakDoc, moodsDoc } from '../db';
import { UserProfile, Screen } from '../App';
import PetCharacter, { CharacterConfig, DEFAULT_CONFIG } from './PetCharacter';
import AiCompanionPanel from './AiCompanionPanel';
import { ActivityFeedItem, recordActivity } from '../utils/activityFeed';
import { buildPetMemory } from '../utils/relationshipInsights';

interface Props { user: UserProfile; navigate?: (s: Screen) => void; mini?: boolean; backTo?: Screen; }

type ActionType = 'feed' | 'wash' | 'play' | 'medicine' | 'pet' | 'dance' | 'story';

interface BondMoment {
  text: string;
  byName: string;
  date: string;
  action: ActionType;
}

interface PetState {
  happiness: number;
  cleanliness: number;
  hunger: number;
  lastFed: any;
  lastWashed: any;
  lastPlayed: any;
  lastActive: any;
  lastCareAt?: any;
  lastCareDecayAt?: any;
  totalDays: number;
  level: number;
  levelUpDate: any;
  showLevelUp?: boolean;
  xp?: number;
  streakMilestone?: number;
  streakBroken?: boolean;
  characterConfig?: CharacterConfig;
  lastMedicine?: any;
  bond?: number;
  lastBondAt?: any;
  lastBondMoment?: BondMoment;
  lastBondDecayDate?: string;
  bondDate?: string;
  dailyBondGain?: number;
  lastRitualDate?: string;
  xpDate?: string;
  dailyCareXp?: number;
  dailyCareCount?: number;
}

interface StreakState { count: number; broken?: boolean; }
interface MoodEntry { mood: string; label: string; }
interface MoodData { [dateKey: string]: { [uid: string]: MoodEntry }; }

const LEVELS = ['Yavru', 'Minik', 'Çocuk', 'Genç', 'Yetişkin'];
const LEVEL_EMOJIS = ['🌱', '🌿', '🌸', '🌺', '🌹'];
const XP_MAX = 620;
const DAILY_CARE_XP_CAP = 4;
const DAILY_BOND_GAIN_CAP = 6;
const STREAK_XP_BONUS = 24;

const RANDOM_MSGS = [
  'Bugün nasılsınız? 🌸',
  'Sizi görünce mutlu oluyorum 💜',
  'Bugün birbirinize sarıldınız mı? 🤗',
  'İkinizi de seviyorum! 💕',
  'Çok şanslıyım, böyle bir çiftim var 🥰',
  'Her gün sizi görmek güzel 🌈',
  'Şu an gülümsüyor musunuz? 😊',
];

function isSleepTime() {
  const h = new Date().getHours();
  return h >= 23 || h < 8;
}

function getTodayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function toDativeName(name: string) {
  const lower = name.toLocaleLowerCase('tr-TR');
  const vowels = lower.match(/[aeıioöuü]/g);
  const lastVowel = vowels?.[vowels.length - 1] || 'a';
  const front = ['e', 'i', 'ö', 'ü'].includes(lastVowel);
  const endsWithVowel = /[aeıioöuü]$/i.test(lower);
  return `${name}'${endsWithVowel ? (front ? 'ye' : 'ya') : (front ? 'e' : 'a')}`;
}

function toGenitiveName(name: string) {
  const lower = name.toLocaleLowerCase('tr-TR');
  const vowels = lower.match(/[aeıioöuü]/g);
  const lastVowel = vowels?.[vowels.length - 1] || 'a';
  const suffix = ['a', 'ı'].includes(lastVowel)
    ? 'ın'
    : ['e', 'i'].includes(lastVowel)
      ? 'in'
      : ['o', 'u'].includes(lastVowel)
        ? 'un'
        : 'ün';
  const endsWithVowel = /[aeıioöuü]$/i.test(lower);
  return `${name}'${endsWithVowel ? `n${suffix}` : suffix}`;
}

interface StatusInfo { emoji: string; label: string; halo: string; halo2: string; mood: 'normal' | 'happy' | 'sad'; sick?: boolean; }
interface CareFocus {
  action: ActionType;
  title: string;
  detail: string;
  emoji: string;
}

function isSickPet(pet: PetState) {
  const lastActive = pet.lastActive?.toMillis?.() || 0;
  const daysSinceActive = (Date.now() - lastActive) / 86400000;
  const weakStats = pet.hunger < 14 && pet.cleanliness < 18 && pet.happiness < 22;
  return daysSinceActive > 4 || weakStats;
}

function getStatus(pet: PetState): StatusInfo {
  const now = Date.now();
  const lastActive = pet.lastActive?.toMillis?.() || 0;
  if (isSickPet(pet))
    return { emoji: '✚', label: 'Hasta düştü', halo: '#EF4444', halo2: '#7F1D1D', mood: 'sad', sick: true };
  if ((now - lastActive) / 86400000 > 2)
    return { emoji: '😢', label: 'Üzgün hissediyor', halo: '#5C6BC0', halo2: '#1A237E', mood: 'sad' };
  if (pet.hunger < 20)
    return { emoji: '😫', label: 'Aç kaldı', halo: '#FF9800', halo2: '#E65100', mood: 'sad' };
  if (pet.cleanliness < 20)
    return { emoji: '🤧', label: 'Kirli', halo: '#795548', halo2: '#3E2723', mood: 'normal' };
  if (pet.happiness > 70)
    return { emoji: '😊', label: 'Mutlu hissediyor', halo: '#FF6B9D', halo2: '#FFD700', mood: 'happy' };
  return { emoji: '🙂', label: 'İyi hissediyor', halo: '#C77DFF', halo2: '#7B1FA2', mood: 'normal' };
}

function buildBubble(streak: StreakState | null, moods: MoodData, lastActive: any, uid: string): string {
  const h = new Date().getHours(), m = new Date().getMinutes();
  if (h === 8 && m <= 30) return 'Günaydın! Uyandım, beni besler misin? 🌅';
  const today = getTodayKey();
  const entry = moods[today] || {};
  const mine = entry[uid];
  const partnerEntry = Object.entries(entry).find(([id]) => id !== uid)?.[1];
  const hoursAgo = lastActive?.toMillis ? (Date.now() - lastActive.toMillis()) / 3600000 : 999;

  if (h >= 22) return 'Bu saatte hâlâ uyumadınız mı? 😴';
  if (streak?.broken) return 'Dün soruyu unuttunuz... 🥺';
  if ((streak?.count || 0) >= 7) return 'Bu hafta çok iyiydik! 🥰';
  if (hoursAgo > 24) return 'Özledim sizi... neredeydiniz? 💔';
  if (mine && partnerEntry) {
    if (mine.label === partnerEntry.label) return 'Bugün ikiniz de aynı hissediyorsunuz 💕';
    const happy = ['Mutlu', 'Huzurlu', 'Yakın', 'Özledim'];
    if (happy.includes(mine.label) !== happy.includes(partnerEntry.label))
      return 'Birinin moralini düzeltmeye ne dersiniz? 🤗';
  }
  return RANDOM_MSGS[Math.floor(Math.random() * RANDOM_MSGS.length)];
}

function getDream(userName: string) {
  const partner = userName === 'Serhat' ? 'Ayça' : 'Serhat';
  const dreams = [
    `${userName} ve ${partner} ile sahilde ışık topladım`,
    `Bugün ikinizin yanında küçük bir gezegende uyudum`,
    `${partner} bana masal anlattı, ${userName} battaniye getirdi`,
    `Birlikte yıldızlardan salıncak yaptık`,
  ];
  return dreams[Math.floor(Math.random() * dreams.length)];
}

function getSeason() {
  const month = new Date().getMonth() + 1;
  if ([12, 1, 2].includes(month)) return 'winter';
  if ([3, 4, 5].includes(month)) return 'spring';
  if ([6, 7, 8].includes(month)) return 'summer';
  return 'autumn';
}

function toTimeLabel(timestamp: any, fallback: string) {
  const raw = timestamp?.toMillis?.()
    ?? (timestamp?.seconds ? timestamp.seconds * 1000 : timestamp instanceof Date ? timestamp.getTime() : 0);
  if (!raw) return fallback;
  const diff = Math.max(0, Date.now() - raw);
  const minute = 60000;
  const hour = 3600000;
  const day = 86400000;
  if (diff < minute) return 'az önce';
  if (diff < hour) return `${Math.floor(diff / minute)} dk önce`;
  if (diff < day) return `${Math.floor(diff / hour)} saat önce`;
  return `${Math.floor(diff / day)} gün önce`;
}

function clampStat(value: number) {
  return Math.min(100, Math.max(0, Math.round(value)));
}

function getBondProfile(bond: number, petName: string) {
  if (bond >= 86) {
    return {
      label: 'Aileden biri',
      detail: `${petName} sizi ayak sesinizden tanıyor gibi. İlgi görünce günü hemen parlıyor.`,
      tone: 'Derin bağ',
    };
  }
  if (bond >= 62) {
    return {
      label: 'Size bağlandı',
      detail: `${petName} artık sadece bakılmayı değil, birlikte geçirilen küçük anları bekliyor.`,
      tone: 'Yakın',
    };
  }
  if (bond >= 34) {
    return {
      label: 'Alışıyor',
      detail: 'Her sevme, oyun ve masal onun için küçük bir hatıraya dönüşüyor.',
      tone: 'Büyüyor',
    };
  }
  return {
    label: 'Yeni yeni güveniyor',
    detail: 'Düzenli gelince daha çok tepki verecek, daha çok şey hatırlayacak.',
    tone: 'İlk bağ',
  };
}

function getMoodInsight(moods: MoodData, uid: string, partner: string, petName: string) {
  const today = getTodayKey();
  const entry = moods[today] || {};
  const mine = entry[uid];
  const partnerMood = Object.entries(entry).find(([id]) => id !== uid)?.[1];

  if (mine && partnerMood) {
    if (mine.label === partnerMood.label) {
      return `Bugün ikiniz de "${mine.label}" tarafındasınız. ${petName} bunu sakin bir ortak alan gibi hissediyor.`;
    }
    return `Bugün sen "${mine.label}", ${partner} "${partnerMood.label}". ${petName} aradaki farkı yumuşatmak ister gibi davranıyor.`;
  }
  if (mine) return `Senin bugünkü halin "${mine.label}". ${partner} da ekleyince ${petName} ikinizin ritmini daha iyi okuyacak.`;
  if (partnerMood) return `${partner} bugün "${partnerMood.label}" bırakmış. Sen de seçince ${petName} ikinizi birlikte yorumlayacak.`;
  return `Bugünün ruh hali henüz boş. ${petName} önce nasıl olduğunuzu anlamak istiyor.`;
}

function getDailyRitual(userName: string, partner: string, today: string, petName: string) {
  const petTo = toDativeName(petName);
  const rituals = [
    {
      title: 'Bir cümleyi ona emanet edin',
      detail: `${userName} ve ${partner} bugün birbirinize söylemek istediğiniz tek kelimeyi içinizden geçirip ${petTo} masal bıraksın.`,
      action: 'story' as ActionType,
      cta: 'Masal bırak',
      emoji: '✦',
    },
    {
      title: 'On saniyelik ortak dans',
      detail: `Bir şarkının minicik bir yerini açın. ${petName} dans ederken ikiniz de aynı anı işaretlemiş olun.`,
      action: 'dance' as ActionType,
      cta: 'Dans ettir',
      emoji: '♪',
    },
    {
      title: 'Sakin sevgi molası',
      detail: `Ekrana acele etmeden dokunun. Bugünün en yumuşak yerini ${petName} aklına yazsın.`,
      action: 'pet' as ActionType,
      cta: 'Sevgi ver',
      emoji: '♡',
    },
    {
      title: 'Küçük neşe işareti',
      detail: 'Ona oyun yaptırın. Sonra bugün sizi gülümseten en ufak şeyi hatırlayın.',
      action: 'play' as ActionType,
      cta: 'Oynat',
      emoji: '✨',
    },
  ];
  const seed = today.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return rituals[seed % rituals.length];
}

function getBondMomentText(type: ActionType, source: 'care' | 'ritual' | 'toy', name: string, partner: string, cfg: CharacterConfig) {
  const petName = cfg.name || 'Minik';
  const petTo = toDativeName(petName);
  if (source === 'ritual') {
    return `${name}, ${partner} ile aranızdaki küçük bir anı ${petTo} emanet etti.`;
  }
  if (type === 'feed') return `${name} onun karnını doyurdu; ${petName} bunu evde biri var hissi gibi sakladı.`;
  if (type === 'wash') return `${name} onu toparladı; üstünden günün ağırlığı kalkmış gibi oldu.`;
  if (type === 'play') return `${name} onunla oynadı; içeride küçük bir kahkaha kaldı.`;
  if (type === 'pet') return `${name} onu sevdi; ${petName} biraz daha size ait hissetti.`;
  if (type === 'dance') return `${name} onu dans ettirdi; bu anı parlak ve komik bir hatıra diye kaydetti.`;
  if (type === 'story') return `${name} ona masal bıraktı; ${partner}'ın adı da rüyasının kenarında durdu.`;
  if (type === 'medicine') return `${name} onu iyileştirdi; güven duygusu biraz daha yerine oturdu.`;
  return `${name} bugün onun yanında durdu.`;
}

function getTimestampMillis(timestamp: any) {
  return timestamp?.toMillis?.()
    ?? (timestamp?.seconds ? timestamp.seconds * 1000 : timestamp instanceof Date ? timestamp.getTime() : 0);
}

function getBaseXpGain(type: ActionType, source: 'care' | 'ritual' | 'toy', ritualAlreadyDone: boolean) {
  if (source === 'ritual') return ritualAlreadyDone ? 1 : 8;
  if (type === 'medicine') return 2;
  if (source === 'toy') return 1;
  return 1;
}

function getRawBondGain(type: ActionType, source: 'care' | 'ritual' | 'toy', ritualAlreadyDone: boolean) {
  if (source === 'ritual') return ritualAlreadyDone ? 1 : 5;
  if (type === 'pet' || type === 'story') return 2;
  if (type === 'play' || type === 'medicine') return 2;
  return 1;
}

function getCareFocus(pet: PetState): CareFocus {
  if (isSickPet(pet)) {
    return { action: 'medicine', title: 'Önce biraz toparlayalım', detail: 'İlaç, biraz dinlenme ve sevgi iyi gelir.', emoji: '✚' };
  }
  if (pet.hunger < 20) {
    return { action: 'feed', title: 'Karnı acıkmış', detail: 'Bir şeyler yedirmek şu an en iyi başlangıç.', emoji: '🍭' };
  }
  if (pet.cleanliness < 20) {
    return { action: 'wash', title: 'Temizlenmek istiyor', detail: 'Kısa bir bakım onu hemen toparlar.', emoji: '🛁' };
  }
  if (pet.happiness < 35) {
    return { action: 'play', title: 'Biraz moral istiyor', detail: 'Bir oyun ya da minik ilgi onu hızla açar.', emoji: '🎮' };
  }
  return { action: 'play', title: 'Bugün keyfi yerinde', detail: 'İstersen ufak bir oyunla günü daha da güzelleştirin.', emoji: '✨' };
}

// ── Particles (unchanged) ──────────────────────────────────
type Particle = { id: number; x: number; y: number; delay: number; color?: string; size: number; rotate?: number };

function StarParticles() {
  const p = useMemo(() => Array.from({ length: 8 }, (_, i) => ({
    id: i, x: (Math.random() - 0.5) * 120, y: -(50 + Math.random() * 80),
    delay: Math.random() * 0.3, size: 14 + Math.random() * 10,
  })), []);
  return <>{p.map(q => (
    <motion.span key={q.id} initial={{ x: 0, y: 0, opacity: 1, scale: 0.4 }}
      animate={{ x: q.x, y: q.y, opacity: 0, scale: 1.1 }}
      transition={{ duration: 0.7, delay: q.delay, ease: 'easeOut' }}
      style={{ position: 'absolute', fontSize: q.size, pointerEvents: 'none' }}>⭐</motion.span>
  ))}</>;
}

function DropParticles() {
  const p = useMemo(() => Array.from({ length: 7 }, (_, i) => ({
    id: i, x: (Math.random() - 0.5) * 90, y: 40 + Math.random() * 60,
    delay: Math.random() * 0.35, size: 12 + Math.random() * 8,
  })), []);
  return <>{p.map(q => (
    <motion.span key={q.id} initial={{ x: 0, y: 0, opacity: 1, scale: 0.3 }}
      animate={{ x: q.x, y: q.y, opacity: 0, scale: 1 }}
      transition={{ duration: 0.65, delay: q.delay, ease: 'easeIn' }}
      style={{ position: 'absolute', fontSize: q.size, pointerEvents: 'none' }}>💧</motion.span>
  ))}</>;
}

function ConfettiParticles() {
  const COLORS = ['#FF6B9D', '#C77DFF', '#FFD700', '#7dff8f', '#00E5FF', '#FF9800'];
  const p: Particle[] = useMemo(() => Array.from({ length: 36 }, (_, i) => ({
    id: i, x: (Math.random() * 280) - 140, y: -(100 + Math.random() * 200),
    delay: Math.random() * 0.7, color: COLORS[Math.floor(Math.random() * COLORS.length)],
    size: 6 + Math.random() * 7, rotate: Math.random() * 360,
  })), []); // eslint-disable-line
  return <>{p.map(q => (
    <motion.div key={q.id} initial={{ x: 0, y: 20, opacity: 1, rotate: 0 }}
      animate={{ x: q.x, y: q.y, opacity: 0, rotate: (q.rotate || 0) + 540 }}
      transition={{ duration: 1.1 + q.delay * 0.5, delay: q.delay * 0.4, ease: 'easeOut' }}
      style={{ position: 'absolute', width: q.size, height: q.size * 0.55, background: q.color, borderRadius: 2, pointerEvents: 'none' }} />
  ))}</>;
}

function FullConfetti() {
  const COLORS = ['#FF6B9D', '#C77DFF', '#FFD700', '#7dff8f', '#00E5FF', '#FF9800'];
  const p: Particle[] = useMemo(() => Array.from({ length: 50 }, (_, i) => ({
    id: i, x: Math.random() * 100, y: -20, delay: Math.random() * 0.8,
    size: 6 + Math.random() * 8, color: COLORS[Math.floor(Math.random() * COLORS.length)],
    rotate: Math.random() * 360,
  })), []); // eslint-disable-line
  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 500, overflow: 'hidden' }}>
      {p.map(q => (
        <motion.div key={q.id}
          initial={{ x: `${q.x}vw`, y: -20, opacity: 1, rotate: q.rotate }}
          animate={{ y: '110vh', opacity: 0, rotate: (q.rotate || 0) + 540 }}
          transition={{ duration: 2 + q.delay, delay: q.delay, ease: [0.25, 0.46, 0.45, 0.94] }}
          style={{ position: 'absolute', width: q.size, height: q.size * 0.55, background: q.color, borderRadius: 2 }} />
      ))}
    </div>
  );
}

function SeasonEffect({ season }: { season: string }) {
  const items = useMemo(() => Array.from({ length: season === 'winter' ? 12 : 8 }, (_, i) => ({
    id: i,
    left: 8 + Math.random() * 84,
    delay: Math.random() * 3,
    duration: 4 + Math.random() * 3,
  })), [season]);

  if (season === 'summer') {
    return <motion.div style={S.seasonSun} animate={{ rotate: 360 }} transition={{ duration: 24, repeat: Infinity, ease: 'linear' }} />;
  }

  return (
    <div style={S.seasonLayer}>
      {items.map(item => (
        <motion.span
          key={item.id}
          style={{
            ...S.seasonParticle,
            left: `${item.left}%`,
            color: season === 'winter' ? 'rgba(255,255,255,0.72)' : season === 'spring' ? '#F0ABFC' : '#A78BFA',
          }}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 300, opacity: [0, 0.9, 0] }}
          transition={{ duration: item.duration, delay: item.delay, repeat: Infinity, ease: 'linear' }}
        >
          {season === 'winter' ? '•' : season === 'spring' ? '✦' : '⌁'}
        </motion.span>
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────
export default function OurLittleOne({ user, navigate, mini = false, backTo = 'home' }: Props) {
  const [pet, setPet] = useState<PetState | null>(null);
  const [streak, setStreak] = useState<StreakState | null>(null);
  const [moods, setMoods] = useState<MoodData>({});
  const [activityFeed, setActivityFeed] = useState<ActivityFeedItem[]>([]);
  const [sleeping, setSleeping] = useState(isSleepTime);
  const [sleepPokeMsg, setSleepPokeMsg] = useState('');
  const [bubble, setBubble] = useState('');
  const [dream, setDream] = useState('');
  const [levelUpAnim, setLevelUpAnim] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [streakBonusAnim, setStreakBonusAnim] = useState(0);
  const [streakBrokenAnim, setStreakBrokenAnim] = useState(false);
  const [actionParticles, setActionParticles] = useState<ActionType | null>(null);
  const [particleKey, setParticleKey] = useState(0);
  const [showLevelConfetti, setShowLevelConfetti] = useState(false);
  const [showMilestoneConfetti, setShowMilestoneConfetti] = useState(false);
  const petControls = useAnimation();
  const bubbleShownRef = useRef(false);
  const bubbleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sleepTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streakRef = useRef(streak);
  const moodsRef = useRef(moods);
  const partner = user.displayName === 'Serhat' ? 'Ayça' : 'Serhat';
  const todayKey = getTodayKey();

  useEffect(() => { streakRef.current = streak; }, [streak]);
  useEffect(() => { moodsRef.current = moods; }, [moods]);
  useEffect(() => () => {
    if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
    if (sleepTimerRef.current) clearTimeout(sleepTimerRef.current);
  }, []);

  const showBubble = useCallback((message: string, duration = 11000) => {
    if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
    setBubble(message);
    bubbleTimerRef.current = setTimeout(() => setBubble(''), duration);
  }, []);

  useEffect(() => {
    return onSnapshot(streakDoc(user.coupleId), snap => {
      if (snap.exists()) setStreak(snap.data() as StreakState);
    });
  }, [user.coupleId]);

  useEffect(() => {
    return onSnapshot(moodsDoc(user.coupleId), snap => {
      if (snap.exists()) setMoods(snap.data() as MoodData);
    });
  }, [user.coupleId]);

  useEffect(() => {
    const q = query(activityFeedColl(user.coupleId), orderBy('createdAt', 'desc'), limit(18));
    return onSnapshot(q, snap => {
      setActivityFeed(snap.docs.map(d => ({ id: d.id, ...d.data() } as ActivityFeedItem)));
    });
  }, [user.coupleId]);

  useEffect(() => {
    return onSnapshot(petDoc(user.coupleId), snap => {
      if (snap.exists()) {
        const data = snap.data() as PetState;
        setPet(data);
        if (data.showLevelUp) {
          setLevelUpAnim(true); setShowLevelConfetti(true);
          setTimeout(() => { setLevelUpAnim(false); setShowLevelConfetti(false); }, 5000);
          updateDoc(petDoc(user.coupleId), { showLevelUp: false });
        }
        if (data.streakMilestone && data.streakMilestone > 0) {
          setStreakBonusAnim(data.streakMilestone); setShowMilestoneConfetti(true);
          const earned = (data.xp || 0) + STREAK_XP_BONUS;
          const leveled = earned >= XP_MAX && data.level < 4;
          const upd: Record<string, any> = { streakMilestone: 0, xp: leveled ? earned - XP_MAX : Math.min(earned, XP_MAX - 1) };
          if (leveled) { upd.level = data.level + 1; upd.levelUpDate = serverTimestamp(); upd.showLevelUp = true; }
          updateDoc(petDoc(user.coupleId), upd);
          setTimeout(() => { setStreakBonusAnim(0); setShowMilestoneConfetti(false); }, 5000);
        }
        if (data.streakBroken) {
          setStreakBrokenAnim(true);
          updateDoc(petDoc(user.coupleId), { streakBroken: false });
          setTimeout(() => setStreakBrokenAnim(false), 4500);
        }
      } else {
        setDoc(petDoc(user.coupleId), {
          happiness: 70, cleanliness: 70, hunger: 70,
          bond: 18,
          totalDays: 0, level: 0, xp: 0,
          lastFed: serverTimestamp(), lastWashed: serverTimestamp(),
          lastPlayed: serverTimestamp(), lastActive: serverTimestamp(),
          lastCareAt: serverTimestamp(), lastCareDecayAt: serverTimestamp(), lastBondAt: serverTimestamp(),
          lastBondDecayDate: getTodayKey(), bondDate: getTodayKey(),
          dailyBondGain: 0, xpDate: getTodayKey(), dailyCareXp: 0, dailyCareCount: 0,
          levelUpDate: serverTimestamp(), showLevelUp: false,
        });
      }
    });
  }, [user.coupleId]);

  // Show speech bubble once on first pet load (60% chance)
  useEffect(() => {
    if (!pet || mini || bubbleShownRef.current) return;
    bubbleShownRef.current = true;
    if (Math.random() >= 0.6) return;
    const t = setTimeout(() => {
      const msg = buildBubble(streakRef.current, moodsRef.current, pet.lastActive, user.uid);
      showBubble(msg);
    }, 1000);
    return () => clearTimeout(t);
  }, [pet, mini, user.uid, showBubble]);

  // Decay
  useEffect(() => {
    if (!pet) return;
    const applyDecay = async () => {
      const now = Date.now();
      const updates: Record<string, any> = {};
      const lastCareDecay = getTimestampMillis(pet.lastCareDecayAt) || getTimestampMillis(pet.lastActive) || now;
      const hoursSinceCareDecay = Math.min(24, Math.max(0, (now - lastCareDecay) / 3600000));

      if (hoursSinceCareDecay >= 0.25) {
        updates.hunger = Math.max(0, pet.hunger - hoursSinceCareDecay * 1.8);
        updates.cleanliness = Math.max(0, pet.cleanliness - hoursSinceCareDecay * 1.1);
        updates.happiness = Math.max(0, pet.happiness - hoursSinceCareDecay * 0.7);
        updates.lastCareDecayAt = serverTimestamp();
      }

      const today = getTodayKey();
      const lastBond = getTimestampMillis(pet.lastBondAt) || getTimestampMillis(pet.lastActive) || now;
      const daysSinceBond = Math.floor((now - lastBond) / 86400000);
      if (pet.lastBondDecayDate !== today && daysSinceBond > 0) {
        updates.bond = clampStat((pet.bond ?? 18) - Math.min(18, daysSinceBond * 4));
        updates.lastBondDecayDate = today;
      }

      if (Object.keys(updates).length) await updateDoc(petDoc(user.coupleId), updates);
    };
    applyDecay();
    const id = setInterval(applyDecay, 60000);
    return () => clearInterval(id);
  }, [pet, user.coupleId]);

  // Re-check sleep time every minute
  useEffect(() => {
    const id = setInterval(() => setSleeping(isSleepTime()), 60000);
    return () => clearInterval(id);
  }, []);

  const pokeSleep = () => {
    setSleepPokeMsg('Uyandırma beni... çok güzel bir rüya görüyordum');
    if (sleepTimerRef.current) clearTimeout(sleepTimerRef.current);
    sleepTimerRef.current = setTimeout(() => setSleepPokeMsg(''), 8500);
  };

  const petTapReaction = () => {
    if (sleeping) {
      pokeSleep();
      return;
    }
    const reactions = [
      'Bana baktınız ya, tamam, mutluyum.',
      'Beni fark ettiğinizde biraz büyüyorum gibi.',
      'Bir dokunuş, bir gülümseme.',
      'Bugün de sevgi topluyorum.',
      'Aynen böyle, biraz daha ilgi.',
    ];
    showBubble(reactions[Math.floor(Math.random() * reactions.length)]);
    petControls.start({ scale: [1, 1.04, 1], y: [0, -6, 0], transition: { duration: 0.45, ease: 'easeOut' } });
  };

  const action = useCallback(async (type: ActionType, source: 'care' | 'ritual' | 'toy' = 'care') => {
    if (sleeping && type !== 'medicine' && type !== 'story') return;
    const updates: Record<string, any> = { lastActive: serverTimestamp(), lastCareAt: serverTimestamp(), lastCareDecayAt: serverTimestamp() };
    setParticleKey(k => k + 1);
    const currentXp = pet?.xp || 0;
    const xpDayReset = pet?.xpDate !== todayKey;
    const dailyCareXp = xpDayReset ? 0 : pet?.dailyCareXp || 0;
    const dailyCareCount = xpDayReset ? 0 : pet?.dailyCareCount || 0;
    const currentBond = pet?.bond ?? 18;
    const ritualAlreadyDone = source === 'ritual' && pet?.lastRitualDate === todayKey;
    const baseXpGain = getBaseXpGain(type, source, ritualAlreadyDone);
    const xpGain = source === 'ritual'
      ? baseXpGain
      : Math.min(baseXpGain, Math.max(0, DAILY_CARE_XP_CAP - dailyCareXp));
    const bondDayReset = pet?.bondDate !== todayKey;
    const dailyBondGain = bondDayReset ? 0 : pet?.dailyBondGain || 0;
    const rawBondGain = getRawBondGain(type, source, ritualAlreadyDone);
    const bondGain = Math.min(rawBondGain, Math.max(0, DAILY_BOND_GAIN_CAP - dailyBondGain));
    updates.xpDate = todayKey;
    updates.dailyCareXp = source === 'ritual' ? dailyCareXp : dailyCareXp + xpGain;
    updates.dailyCareCount = dailyCareCount + 1;
    updates.bondDate = todayKey;
    updates.dailyBondGain = dailyBondGain + bondGain;
    updates.bond = clampStat(currentBond + bondGain);
    updates.lastBondAt = serverTimestamp();
    updates.lastBondDecayDate = todayKey;
    updates.lastBondMoment = {
      text: getBondMomentText(type, source, user.displayName, partner, pet?.characterConfig || DEFAULT_CONFIG),
      byName: user.displayName,
      date: todayKey,
      action: type,
    };
    if (source === 'ritual') updates.lastRitualDate = todayKey;

    if (type === 'feed') {
      updates.hunger = Math.min(100, (pet?.hunger || 0) + 30);
      updates.happiness = Math.min(100, (pet?.happiness || 0) + 5);
      updates.lastFed = serverTimestamp();
      setActionMsg('😋 Yem yedi!');
      petControls.start({ y: [0, -28, -18, -34, -12, 0], transition: { duration: 0.85, ease: 'easeOut' } });
      setActionParticles('feed');
    } else if (type === 'wash') {
      updates.cleanliness = Math.min(100, (pet?.cleanliness || 0) + 40);
      updates.happiness = Math.min(100, (pet?.happiness || 0) + 5);
      updates.lastWashed = serverTimestamp();
      setActionMsg('🛁 Yıkandı!');
      petControls.start({ x: [0, -12, 12, -9, 9, -5, 5, 0], transition: { duration: 0.55 } });
      setActionParticles('wash');
    } else if (type === 'medicine') {
      updates.hunger = Math.max(55, pet?.hunger || 0);
      updates.cleanliness = Math.max(55, pet?.cleanliness || 0);
      updates.happiness = Math.max(60, pet?.happiness || 0);
      updates.lastMedicine = serverTimestamp();
      updates.lastFed = serverTimestamp();
      updates.lastWashed = serverTimestamp();
      updates.lastPlayed = serverTimestamp();
      setActionMsg('İlacını aldı, biraz daha iyi');
      petControls.start({ scale: [1, 0.92, 1.08, 1], transition: { duration: 0.55 } });
      setActionParticles('play');
    } else if (type === 'pet') {
      updates.happiness = Math.min(100, (pet?.happiness || 0) + 12);
      updates.lastPlayed = serverTimestamp();
      setActionMsg('Sevildi, içi ısındı.');
      showBubble('Biraz daha kalın yanımda.');
      petControls.start({ scale: [1, 1.08, 0.98, 1.05, 1], y: [0, -5, 0], transition: { duration: 0.75, ease: 'easeOut' } });
      setActionParticles('play');
    } else if (type === 'dance') {
      updates.happiness = Math.min(100, (pet?.happiness || 0) + 18);
      updates.hunger = Math.max(0, (pet?.hunger || 0) - 3);
      updates.lastPlayed = serverTimestamp();
      setActionMsg('Küçük dans başladı.');
      showBubble('Bunu sahne performansı sayıyorum.');
      petControls.start({ x: [0, -18, 18, -14, 14, 0], rotate: [0, -8, 8, -6, 6, 0], transition: { duration: 0.9, ease: 'easeInOut' } });
      setActionParticles('play');
    } else if (type === 'story') {
      updates.happiness = Math.min(100, (pet?.happiness || 0) + 8);
      updates.lastPlayed = serverTimestamp();
      const story = getDream(user.displayName);
      setDream(story);
      setActionMsg(sleeping ? 'Rüyasına küçük bir masal eklendi.' : 'Masal dinledi.');
      showBubble(sleeping ? 'Mmm... devamını rüyamda gördüm.' : story, 12000);
      petControls.start({ y: [0, -8, 0], scale: [1, 1.03, 1], transition: { duration: 0.7 } });
      setActionParticles('feed');
    } else {
      updates.happiness = Math.min(100, (pet?.happiness || 0) + 25);
      updates.lastPlayed = serverTimestamp();
      setActionMsg('Oynadı!');
      petControls.start({ rotate: [0, 360], transition: { duration: 0.65, ease: 'easeInOut' } });
      setActionParticles('play');
    }
    if (source === 'ritual') {
      const petName = pet?.characterConfig?.name || DEFAULT_CONFIG.name;
      setActionMsg(ritualAlreadyDone ? 'Bugünün ritüeline bir küçük iz daha eklendi.' : `Bugünün ritüeli ${toDativeName(petName)} yazıldı.`);
      showBubble(updates.lastBondMoment.text, 12500);
    }
    const nextXp = currentXp + xpGain;
    if (nextXp >= XP_MAX && (pet?.level || 0) < 4) {
      updates.xp = nextXp - XP_MAX;
      updates.level = (pet?.level || 0) + 1;
      updates.levelUpDate = serverTimestamp();
      updates.showLevelUp = true;
    } else {
      updates.xp = Math.min(nextXp, XP_MAX - 1);
    }
    await updateDoc(petDoc(user.coupleId), updates);
    const petName = pet?.characterConfig?.name || DEFAULT_CONFIG.name;
    recordActivity(user, {
      type: source === 'ritual' ? 'pet_ritual' : 'pet_care',
      title: source === 'ritual' ? 'Bugünkü bağ görevi yapıldı' : `${petName} ile ilgilenildi`,
      body: updates.lastBondMoment.text,
      dayKey: todayKey,
      sourceId: user.uid,
    }).catch(() => {});
    setTimeout(() => { setActionMsg(''); setActionParticles(null); }, 3200);
  }, [pet, user, partner, todayKey, petControls, sleeping, showBubble]);

  useEffect(() => {
    if (!sleeping || mini) {
      setDream('');
      return;
    }
    setDream(getDream(user.displayName));
    const id = setInterval(() => setDream(getDream(user.displayName)), 9000);
    return () => clearInterval(id);
  }, [sleeping, mini, user.displayName]);

  const cfg = pet?.characterConfig || DEFAULT_CONFIG;
  const petName = cfg.name || DEFAULT_CONFIG.name;
  const status: StatusInfo = pet
    ? getStatus(pet)
    : { emoji: '…', label: 'Hazırlanıyor', halo: '#C77DFF', halo2: '#7B1FA2', mood: 'normal' };
  const charMood = sleeping ? 'sleeping' : status.mood;
  const levelName = LEVELS[pet?.level ?? 0] || 'Yetişkin';
  const levelEmoji = LEVEL_EMOJIS[pet?.level ?? 0] || '🌹';
  const xp = pet?.xp ?? 0;
  const season = getSeason();
  const bond = pet?.bond ?? 18;
  const bondProfile = useMemo(() => getBondProfile(bond, petName), [bond, petName]);
  const moodInsight = useMemo(() => getMoodInsight(moods, user.uid, partner, petName), [moods, user.uid, partner, petName]);
  const petMemory = useMemo(() => buildPetMemory(activityFeed, petName, partner, user.uid), [activityFeed, petName, partner, user.uid]);
  const dailyRitual = useMemo(() => getDailyRitual(user.displayName, partner, todayKey, petName), [user.displayName, partner, todayKey, petName]);
  const ritualDoneToday = pet?.lastRitualDate === todayKey;
  const lastBondMoment = pet?.lastBondMoment;
  const careFocus = useMemo(() => pet ? getCareFocus(pet) : {
    action: 'play' as ActionType,
    title: 'Minik dost hazır',
    detail: 'Birlikte ilgilenmeye başlayınca burada daha çok şey açılacak.',
    emoji: '✨',
  }, [pet]);
  const careStats = useMemo(() => ([
    {
      label: 'Tokluk',
      value: pet?.hunger ?? 0,
      color: '#FF9800',
    },
    {
      label: 'Temizlik',
      value: pet?.cleanliness ?? 0,
      color: '#00BCD4',
    },
    {
      label: 'Mutluluk',
      value: pet?.happiness ?? 0,
      color: '#E91E8C',
    },
  ]), [pet]);
  const quickFacts = useMemo(() => ([
    { label: 'Son besleme', value: toTimeLabel(pet?.lastFed, 'Henüz yok'), icon: '🍽️' },
    { label: 'Son yıkama', value: toTimeLabel(pet?.lastWashed, 'Henüz yok'), icon: '🧼' },
    { label: 'Son oyun', value: toTimeLabel(pet?.lastPlayed, 'Henüz yok'), icon: '🎲' },
    { label: 'Son bağ', value: toTimeLabel(pet?.lastBondAt, 'Henüz yok'), icon: '♡' },
  ]), [pet?.lastFed, pet?.lastWashed, pet?.lastPlayed, pet?.lastBondAt]);
  const mainActions = useMemo(() => {
    const actions = [
      { type: 'feed' as ActionType, icon: '🍭', label: 'Besle', detail: 'Tokluğu artır', grad: 'linear-gradient(135deg, #FF9800, #FF5722)', shadow: 'rgba(255,152,0,0.35)' },
      { type: 'wash' as ActionType, icon: '🛁', label: 'Temizle', detail: 'Bakım yap', grad: 'linear-gradient(135deg, #2196F3, #00BCD4)', shadow: 'rgba(33,150,243,0.35)' },
      { type: 'pet' as ActionType, icon: '♡', label: 'Sev', detail: 'Bağı güçlendir', grad: 'linear-gradient(135deg, #BF5AF2, #E91E8C)', shadow: 'rgba(191,90,242,0.32)' },
      { type: 'play' as ActionType, icon: '🎮', label: 'Oyna', detail: 'Neşe kat', grad: 'linear-gradient(135deg, #E91E8C, #9C27B0)', shadow: 'rgba(233,30,140,0.35)' },
    ];
    if (!status.sick) return actions;
    return [
      { type: 'medicine' as ActionType, icon: '✚', label: 'İyileştir', detail: 'Önce toparla', grad: 'linear-gradient(135deg, #EF4444, #7C3AED)', shadow: 'rgba(239,68,68,0.35)' },
      ...actions.slice(0, 3),
    ];
  }, [status.sick]);

  if (!pet) return null;

  // ── Mini mode ──
  if (mini) {
    return (
      <div style={miniS.wrap} onClick={() => navigate?.('pet')}>
        <PetCharacter config={cfg} size={58} mood={charMood} />
        <div style={miniS.info}>
          <span style={{ fontSize: 16 }}>{sleeping ? '😴' : status.emoji}</span>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
            {sleeping ? 'Uyuyor' : status.label.split(' ')[0]}
          </span>
        </div>
      </div>
    );
  }

  // ── Full mode ──
  return (
    <div style={S.container}>
      <AnimatePresence>{showLevelConfetti && <FullConfetti key="lv" />}</AnimatePresence>
      <AnimatePresence>{showMilestoneConfetti && <FullConfetti key="ms" />}</AnimatePresence>

      {/* Level up overlay */}
      <AnimatePresence>
        {levelUpAnim && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={S.overlay}>
            <motion.div initial={{ scale: 0.5, y: 40 }} animate={{ scale: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 240, damping: 18 }} style={S.overlayCard}>
              <motion.span animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 0.7, repeat: 3 }} style={{ fontSize: 60 }}>🎉</motion.span>
              <p style={S.overlayTitle}>Seviye Atladı!</p>
              <p style={S.overlaySub}>{levelEmoji} {levelName}</p>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14, marginTop: 8 }}>Birlikte büyüyoruz!</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Streak milestone */}
      <AnimatePresence>
        {streakBonusAnim > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={S.overlay}>
            <motion.div initial={{ scale: 0.5, y: 40 }} animate={{ scale: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 240, damping: 18 }} style={S.overlayCard}>
              <motion.span animate={{ scale: [1, 1.25, 0.95, 1.15, 1] }} transition={{ duration: 0.8, repeat: 3 }} style={{ fontSize: 60 }}>🔥</motion.span>
              <p style={S.overlayTitle}>{streakBonusAnim} Günlük Ritim</p>
              <motion.p animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 0.8, repeat: Infinity }}
                style={{ fontSize: 22, fontWeight: 700, color: '#FFD700', marginTop: 4 }}>+{STREAK_XP_BONUS} XP Kazandın!</motion.p>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14, marginTop: 8 }}>Bugün de ritimde kaldınız.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Streak broken */}
      <AnimatePresence>
        {streakBrokenAnim && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={S.overlay} onClick={() => setStreakBrokenAnim(false)}>
            <motion.div initial={{ scale: 0.5, y: 40 }} animate={{ scale: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 18 }}
              style={{ ...S.overlayCard, borderColor: 'rgba(150,30,30,0.5)', boxShadow: '0 0 60px rgba(180,30,30,0.25)' }}>
              <motion.span animate={{ rotate: [0, -5, 5, -5, 5, 0] }} transition={{ duration: 1, repeat: 2 }} style={{ fontSize: 60 }}>💔</motion.span>
              <p style={{ ...S.overlayTitle, background: 'linear-gradient(135deg, #ff9999, #cc5555)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                Ritim durdu
              </p>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 14, marginTop: 8, textAlign: 'center' }}>
                Bugün ikiniz de cevaplayınca yeniden başlar.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div style={S.header}>
        <button className="back-btn" onClick={() => navigate?.(backTo)}>←</button>
        <div>
          <h1 style={S.title}>{petName}</h1>
          <p style={S.subtitle}>Bizim Minik · {levelEmoji} {levelName}</p>
        </div>
        <button
          style={S.editBtn}
          onClick={() => navigate?.('character-creator')}
          title="Karakteri düzenle"
        >✏️</button>
      </div>

      <div style={S.main}>

        {/* ── CHARACTER AREA ── */}
        <div style={S.charArea} onClick={petTapReaction}>
          {/* Mood halo */}
          <div style={{
            ...S.halo,
            background: sleeping
              ? 'radial-gradient(ellipse at 50% 65%, rgba(63,81,181,0.4) 0%, rgba(26,35,126,0.18) 55%, transparent 80%)'
              : `radial-gradient(ellipse at 50% 65%, ${status.halo}55 0%, ${status.halo2}22 55%, transparent 80%)`,
          }} />
          <SeasonEffect season={season} />

          {/* Sleep effects */}
          {sleeping && (
            <>
              <AnimatePresence>
                {dream && (
                  <motion.div
                    initial={{ opacity: 0, y: 12, scale: 0.92 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.96 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 24 }}
                    style={S.dreamBubble}
                  >
                    {dream}
                  </motion.div>
                )}
              </AnimatePresence>
              {[0, 1, 2].map(i => (
                <motion.span key={i}
                  style={{ position: 'absolute', fontSize: 14 + i * 5, top: 14 - i * 22, right: 58 + i * 18, pointerEvents: 'none', zIndex: 2 }}
                  animate={{ y: [0, -22, 0], opacity: [0, 1, 0] }}
                  transition={{ duration: 2.4, delay: i * 0.8, repeat: Infinity, ease: 'easeInOut' }}>
                  💤
                </motion.span>
              ))}
              {Array.from({ length: 6 }, (_, i) => (
                <motion.span key={`s${i}`}
                  style={{ position: 'absolute', fontSize: 10, left: '50%', top: '50%', pointerEvents: 'none', zIndex: 1 }}
                  animate={{
                    x: Math.cos(i * 1.05) * 88,
                    y: Math.sin(i * 1.05) * 70,
                    opacity: [0, 0.8, 0],
                    rotate: [0, 360],
                  }}
                  transition={{ duration: 3.5, delay: i * 0.55, repeat: Infinity, ease: 'easeInOut' }}>
                  ⭐
                </motion.span>
              ))}
            </>
          )}

          {/* Pet render */}
          <div style={{ position: 'relative', zIndex: 3 }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AnimatePresence>
                {actionParticles === 'feed' && <StarParticles key={`f${particleKey}`} />}
                {actionParticles === 'wash' && <DropParticles key={`w${particleKey}`} />}
                {actionParticles === 'play' && <ConfettiParticles key={`p${particleKey}`} />}
              </AnimatePresence>

              <motion.div animate={petControls} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <motion.div
                  animate={{ scale: [1, 1.016, 1], y: [0, -4, 0] }}
                  transition={{ duration: 3.8, repeat: Infinity, ease: 'easeInOut' }}
                  style={{ display: 'flex' }}
                >
                  <PetCharacter config={cfg} size={144} mood={charMood} />
                </motion.div>
              </motion.div>
            </div>
          </div>

          {/* Status badge */}
          <motion.div
            animate={{ scale: [1, 1.04, 1] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            style={S.statusBadge}
          >
            <span style={{ fontSize: 20 }}>{sleeping ? '😴' : status.emoji}</span>
            <span style={{ fontSize: 13, color: 'white', fontWeight: 600 }}>
              {sleeping ? 'Uyuyor' : status.label}
            </span>
          </motion.div>

          <p style={S.tapHint}>{sleeping ? 'Sadece hafifçe dürtebilirsin.' : 'Dokununca tepki verir.'}</p>

          {/* Speech bubble */}
          <AnimatePresence>
            {(bubble || sleepPokeMsg) && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                style={S.bubble}
                onClick={() => { setBubble(''); setSleepPokeMsg(''); }}
              >
                {sleepPokeMsg || bubble}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div style={S.actions}>
          {mainActions.map(a => (
            <motion.button key={a.type} whileTap={sleeping && a.type !== 'medicine' ? {} : { scale: 0.9 }}
              style={{
                ...S.actionBtn,
                background: sleeping && a.type !== 'medicine' ? 'rgba(255,255,255,0.05)' : a.grad,
                boxShadow: sleeping && a.type !== 'medicine' ? 'none' : `0 6px 20px ${a.shadow}`,
                opacity: sleeping && a.type !== 'medicine' ? 0.45 : 1,
              }}
              onClick={() => action(a.type, a.type === 'pet' ? 'toy' : 'care')}>
              {sleeping && a.type !== 'medicine' ? (
                <>
                  <span style={S.actionIcon}>{a.icon}</span>
                  <span style={S.actionLabel}>Uyuyor</span>
                  <span style={S.actionHint}>{a.label}</span>
                </>
              ) : (
                <>
                  <span style={S.actionIcon}>{a.icon}</span>
                  <span style={S.actionLabel}>{a.label}</span>
                  <span style={S.actionHint}>{a.detail}</span>
                </>
              )}
            </motion.button>
          ))}
        </div>

        {/* Action message */}
        <AnimatePresence>
          {actionMsg && (
            <motion.p initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              style={{ textAlign: 'center', fontSize: 16, color: '#FF6B9D', fontWeight: 600, margin: 0 }}>
              {actionMsg}
            </motion.p>
          )}
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.06 }}
          style={S.todayCard}
        >
          <div style={S.todayTop}>
            <div>
              <p style={S.focusKicker}>Bugün</p>
              <h3 style={S.bondTitle}>{careFocus.title}</h3>
              <p style={S.bondDetail}>{careFocus.detail}</p>
            </div>
            <div style={S.todayStatus}>
              <span>{careFocus.emoji}</span>
              <strong>{bond}</strong>
              <small>bağ</small>
            </div>
          </div>

          <div style={S.compactBars}>
            {careStats.map(stat => (
              <div key={stat.label} style={S.compactStat}>
                <div style={S.compactStatTop}>
                  <span>{stat.label}</span>
                  <b>{Math.round(stat.value)}</b>
                </div>
                <div style={S.careBar}>
                  <span style={{ width: `${Math.min(Math.max(stat.value, 0), 100)}%`, background: stat.color }} />
                </div>
              </div>
            ))}
          </div>

          <div style={S.bondTop}>
            <div>
              <p style={S.focusKicker}>Duygusal bağ</p>
              <h3 style={S.bondTitle}>{bondProfile.label}</h3>
            </div>
            <div style={S.bondScore}>
              <strong>{bond}</strong>
              <small>/100</small>
            </div>
          </div>
          <div style={S.bondBar}>
            <motion.span
              style={S.bondBarFill}
              initial={{ width: 0 }}
              animate={{ width: `${clampStat(bond)}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
          <p style={S.bondDetail}>{bondProfile.detail}</p>
          <div style={S.moodNote}>
            <span style={S.moodNoteTag}>{bondProfile.tone}</span>
            <p style={S.moodNoteText}>{moodInsight}</p>
          </div>

          <div style={S.ritualBox}>
            <div style={S.ritualIcon}>{dailyRitual.emoji}</div>
            <div>
              <strong style={S.ritualTitle}>{dailyRitual.title}</strong>
              <p style={S.ritualText}>{dailyRitual.detail}</p>
            </div>
            <button type="button" style={S.ritualButton} onClick={() => action(sleeping ? 'story' : dailyRitual.action, 'ritual')}>
              {ritualDoneToday ? 'Bir iz daha' : dailyRitual.cta}
            </button>
          </div>

          <div style={S.memoryStrip}>
            <small style={S.memoryKicker}>{toGenitiveName(petName)} aklında kalan</small>
            <p style={S.memoryText}>{lastBondMoment?.text || petMemory}</p>
            {lastBondMoment?.byName && <span style={S.memoryMeta}>{lastBondMoment.byName} · {lastBondMoment.date}</span>}
          </div>
        </motion.div>

        <AiCompanionPanel user={user} compact defaultTab="pet_voice" />

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          style={S.detailsCard}>
          <div style={S.identityHead}>
            <div>
              <p style={S.identityKicker}>Gelişim</p>
              <h3 style={S.identityTitle}>{levelEmoji} {levelName}</h3>
              <small style={S.identitySub}>
                {xp} / {XP_MAX} XP
                {pet.level < 4
                  ? ` · ${toDativeName(LEVELS[pet.level + 1])} ${XP_MAX - xp} XP kaldı`
                  : ' · Maksimum seviye'}
              </small>
            </div>
            <button type="button" style={S.identityButton} onClick={() => navigate?.('character-creator')}>Karakter</button>
          </div>
          <div style={S.xpBar}>
            <span style={{ ...S.xpBarFill, width: `${Math.min(100, Math.max(0, (xp / XP_MAX) * 100))}%` }} />
          </div>
          <div style={S.quickFacts}>
            {quickFacts.map(item => (
              <div key={item.label} style={S.quickFact}>
                <span style={S.quickFactIcon}>{item.icon}</span>
                <div>
                  <small style={S.quickFactLabel}>{item.label}</small>
                  <strong style={S.quickFactValue}>{item.value}</strong>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh' },
  header: { padding: '40px 16px 8px', display: 'flex', alignItems: 'center', gap: 12 },
  title: { fontFamily: "'Inter',sans-serif", fontSize: 22, fontWeight: 850, letterSpacing: '-0.03em', margin: 0 },
  subtitle: { fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 2 },
  editBtn: {
    marginLeft: 'auto',
    width: 42,
    height: 42,
    borderRadius: 16,
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.09)',
    fontSize: 18,
    cursor: 'pointer',
    opacity: 0.78,
    display: 'grid',
    placeItems: 'center',
  },
  main: { padding: '0 16px 118px', display: 'flex', flexDirection: 'column', gap: 12 },

  charArea: {
    position: 'relative', minHeight: 228,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: 7, padding: '6px 0 12px', overflow: 'hidden',
    borderRadius: 28,
    border: '1px solid rgba(255,255,255,0.075)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0.02))',
  },
  halo: {
    position: 'absolute', inset: '-20px -30px',
    pointerEvents: 'none', borderRadius: '50%',
    transition: 'background 1s ease',
  },
  statusBadge: {
    display: 'flex', gap: 6, alignItems: 'center',
    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 24, padding: '6px 16px', zIndex: 3,
  },
  bubble: {
    background: 'rgba(8,8,16,0.86)', border: '1px solid rgba(255,255,255,0.13)',
    borderRadius: 20, padding: '10px 18px', fontSize: 13, color: 'white',
    maxWidth: 260, textAlign: 'center', zIndex: 4, cursor: 'pointer',
    backdropFilter: 'blur(8px)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
  },
  dreamBubble: {
    position: 'absolute',
    top: 24,
    left: 22,
    right: 22,
    zIndex: 5,
    padding: '10px 14px',
    borderRadius: 18,
    background: 'rgba(79,70,229,0.16)',
    border: '1px solid rgba(167,139,250,0.22)',
    color: 'rgba(255,255,255,0.84)',
    fontSize: 12,
    lineHeight: 1.45,
    textAlign: 'center',
    backdropFilter: 'blur(14px)',
  },
  seasonLayer: {
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: 1,
  },
  seasonParticle: {
    position: 'absolute',
    top: -16,
    fontSize: 18,
    textShadow: '0 0 18px currentColor',
  },
  seasonSun: {
    position: 'absolute',
    top: 28,
    right: 28,
    width: 42,
    height: 42,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(250,204,21,0.9), rgba(6,182,212,0.45))',
    boxShadow: '0 0 50px rgba(250,204,21,0.25)',
    opacity: 0.72,
    zIndex: 1,
  },

  tapHint: {
    position: 'relative',
    zIndex: 3,
    color: 'rgba(255,255,255,0.42)',
    fontSize: 12,
    margin: 0,
  },

  focusCard: {
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'linear-gradient(145deg, rgba(191,90,242,0.12), rgba(255,255,255,0.045))',
    borderRadius: 24,
    padding: '16px 16px 14px',
    boxShadow: '0 20px 70px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.06)',
  },
  focusHead: { display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' },
  focusKicker: { color: '#BF5AF2', fontSize: 12, fontWeight: 850, marginBottom: 8, lineHeight: 1 },
  focusTitle: { color: 'white', fontSize: 20, lineHeight: 1.12, fontWeight: 900, letterSpacing: '-0.045em', margin: 0 },
  focusEmoji: {
    width: 44, height: 44, borderRadius: 16, flex: '0 0 44px',
    display: 'grid', placeItems: 'center',
    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)',
    fontSize: 22,
  },
  focusDetail: { color: 'rgba(255,255,255,0.64)', fontSize: 13, lineHeight: 1.46, margin: '10px 0 12px' },
  focusPills: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  todayCard: {
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'linear-gradient(145deg, rgba(255,255,255,0.07), rgba(191,90,242,0.07))',
    borderRadius: 26,
    padding: 16,
    display: 'grid',
    gap: 14,
    boxShadow: '0 24px 76px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)',
  },
  todayTop: { display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'flex-start', gap: 14 },
  todayStatus: {
    width: 74,
    minHeight: 74,
    borderRadius: 22,
    display: 'grid',
    placeItems: 'center',
    padding: 8,
    background: 'rgba(0,0,0,0.18)',
    border: '1px solid rgba(255,255,255,0.08)',
    color: 'white',
    textAlign: 'center',
  },
  compactBars: { display: 'grid', gap: 9 },
  compactStat: { display: 'grid', gap: 6 },
  compactStatTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    color: 'rgba(255,255,255,0.7)',
    fontSize: 12,
    fontWeight: 850,
  },
  bondCard: {
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'linear-gradient(145deg, rgba(255,255,255,0.07), rgba(191,90,242,0.075))',
    borderRadius: 26,
    padding: 16,
    display: 'grid',
    gap: 12,
    boxShadow: '0 24px 76px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)',
  },
  bondTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14 },
  bondTitle: { color: 'white', fontSize: 22, lineHeight: 1.05, fontWeight: 900, letterSpacing: '-0.045em', margin: 0 },
  bondScore: {
    minWidth: 74,
    borderRadius: 20,
    padding: '10px 12px',
    background: 'rgba(0,0,0,0.18)',
    border: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: 2,
  },
  bondBar: {
    height: 10,
    borderRadius: 999,
    background: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
  },
  bondBarFill: {
    display: 'block',
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, #BF5AF2, #FF6B9D, #FFD166)',
    boxShadow: '0 0 22px rgba(191,90,242,0.45)',
  },
  bondDetail: { color: 'rgba(255,255,255,0.64)', fontSize: 13, lineHeight: 1.45, margin: 0 },
  moodNote: {
    display: 'grid',
    gap: 6,
    padding: '12px 13px',
    borderRadius: 18,
    background: 'rgba(0,0,0,0.16)',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  moodNoteTag: { color: '#BF5AF2', fontSize: 12, fontWeight: 900, lineHeight: 1 },
  moodNoteText: { color: 'rgba(255,255,255,0.68)', fontSize: 13, lineHeight: 1.45, margin: 0 },
  ritualBox: {
    display: 'grid',
    gridTemplateColumns: '38px 1fr auto',
    gap: 12,
    alignItems: 'center',
    padding: 12,
    borderRadius: 20,
    background: 'rgba(191,90,242,0.10)',
    border: '1px solid rgba(191,90,242,0.16)',
  },
  ritualIcon: {
    width: 38,
    height: 38,
    borderRadius: 15,
    display: 'grid',
    placeItems: 'center',
    background: 'rgba(255,255,255,0.08)',
    color: '#BF5AF2',
    fontSize: 20,
    fontWeight: 900,
  },
  ritualTitle: { display: 'block', color: 'white', fontSize: 14, lineHeight: 1.2, fontWeight: 900, marginBottom: 5 },
  ritualText: { color: 'rgba(255,255,255,0.56)', fontSize: 12, lineHeight: 1.38, margin: 0 },
  ritualButton: {
    appearance: 'none',
    WebkitAppearance: 'none',
    border: 0,
    borderRadius: 999,
    minHeight: 38,
    padding: '0 12px',
    background: '#BF5AF2',
    color: 'white',
    fontSize: 12,
    fontWeight: 900,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    boxShadow: '0 12px 30px rgba(191,90,242,0.25)',
  },
  memoryStrip: {
    padding: '12px 13px',
    borderRadius: 18,
    background: 'rgba(255,255,255,0.045)',
    border: '1px solid rgba(255,255,255,0.065)',
  },
  memoryKicker: { display: 'block', color: 'rgba(255,255,255,0.42)', fontSize: 11, fontWeight: 900, marginBottom: 6 },
  memoryText: { color: 'rgba(255,255,255,0.76)', fontSize: 13, lineHeight: 1.45, margin: 0 },
  memoryMeta: { display: 'block', color: 'rgba(191,90,242,0.9)', fontSize: 11, fontWeight: 850, marginTop: 8 },
  careGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(108px, 1fr))', gap: 10 },
  careCard: {
    appearance: 'none',
    WebkitAppearance: 'none',
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 20,
    padding: '12px 12px 10px',
    color: 'white',
    textAlign: 'left',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    boxShadow: '0 18px 56px rgba(0,0,0,0.22)',
  },
  careTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  careBar: {
    height: 8,
    borderRadius: 999,
    background: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
  },
  careCardStrong: { fontSize: 13, fontWeight: 800, color: 'white', margin: 0 },
  careCardSmall: { fontSize: 11, color: 'rgba(255,255,255,0.48)', margin: 0, lineHeight: 1.35 },

  playCard: {
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'linear-gradient(145deg, rgba(255,255,255,0.065), rgba(255,255,255,0.035))',
    borderRadius: 24,
    padding: 16,
    boxShadow: '0 20px 70px rgba(0,0,0,0.22)',
    display: 'grid',
    gap: 12,
  },
  playHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 },
  playBadge: {
    borderRadius: 999,
    padding: '8px 10px',
    background: 'rgba(191,90,242,0.14)',
    border: '1px solid rgba(191,90,242,0.2)',
    color: 'rgba(255,255,255,0.86)',
    fontSize: 12,
    fontWeight: 850,
    whiteSpace: 'nowrap',
  },
  toyGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 },
  toyButton: {
    appearance: 'none',
    WebkitAppearance: 'none',
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(0,0,0,0.16)',
    borderRadius: 18,
    minHeight: 96,
    padding: 12,
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    textAlign: 'left',
  },
  toyIcon: {
    width: 30,
    height: 30,
    borderRadius: 12,
    display: 'grid',
    placeItems: 'center',
    background: 'rgba(255,255,255,0.08)',
    color: '#BF5AF2',
    fontSize: 18,
    fontWeight: 900,
  },

  actions: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 },
  actionBtn: {
    border: 'none', borderRadius: 18, padding: '12px 7px',
    minHeight: 84,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  actionLabel: { fontSize: 12, color: 'rgba(255,255,255,0.88)', fontWeight: 600 },
  actionHint: { fontSize: 10, color: 'rgba(255,255,255,0.45)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8 },
  actionIcon: { fontSize: 26, lineHeight: 1 },

  identityCard: {
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 24,
    padding: '16px',
    boxShadow: '0 20px 70px rgba(0,0,0,0.22)',
    display: 'grid',
    gap: 12,
  },
  detailsCard: {
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.045)',
    borderRadius: 24,
    padding: '16px',
    boxShadow: '0 20px 70px rgba(0,0,0,0.2)',
    display: 'grid',
    gap: 12,
  },
  identityHead: { display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' },
  identityKicker: { color: 'rgba(255,255,255,0.46)', fontSize: 12, fontWeight: 850, marginBottom: 8, lineHeight: 1 },
  identityTitle: { color: 'white', fontSize: 18, lineHeight: 1.1, fontWeight: 900, letterSpacing: '-0.04em', margin: 0 },
  identitySub: { display: 'block', color: 'rgba(255,255,255,0.58)', fontSize: 12, lineHeight: 1.35, marginTop: 6 },
  identityButton: {
    border: '1px solid rgba(255,255,255,0.09)',
    background: 'rgba(255,255,255,0.06)',
    borderRadius: 999,
    minHeight: 38,
    padding: '0 14px',
    color: 'white',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  identityChips: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  identityChip: {
    borderRadius: 999,
    padding: '8px 10px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.08)',
    color: 'rgba(235,235,245,0.82)',
    fontSize: 12,
    fontWeight: 800,
  },
  quickFacts: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 },
  quickFact: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    padding: '11px 12px',
    borderRadius: 18,
    background: 'rgba(0,0,0,0.18)',
    border: '1px solid rgba(255,255,255,0.05)',
    minWidth: 0,
    minHeight: 62,
  },
  quickFactLabel: { display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)', margin: 0, lineHeight: 1.15 },
  quickFactValue: { display: 'block', fontSize: 12, lineHeight: 1.2, fontWeight: 800, color: 'white', margin: '4px 0 0' },
  quickFactIcon: { fontSize: 18, flex: '0 0 auto' },
  xpBar: {
    height: 8,
    borderRadius: 999,
    background: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
  },
  xpBarFill: {
    display: 'block',
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, #FFD166, #BF5AF2)',
  },

  xpRow: { display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: '10px 16px' },
  xpText: { fontSize: 13, color: 'rgba(255,255,255,0.65)', margin: 0 },

  growthCard: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 18, padding: '14px 16px 10px' },
  growthTitle: { fontSize: 10, color: 'rgba(255,255,255,0.35)', textAlign: 'center', marginBottom: 14, textTransform: 'uppercase', letterSpacing: 1 },
  growthRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  growthStep: { display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', flex: 1 },
  levelDot: { position: 'absolute', top: -5, width: 6, height: 6, borderRadius: '50%', background: '#FF6B9D' },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  overlayCard: {
    background: 'linear-gradient(135deg, #080810, #17132d)', borderRadius: 28,
    padding: '36px 44px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    border: '1px solid rgba(233,30,140,0.4)', boxShadow: '0 0 80px rgba(233,30,140,0.3)',
  },
  overlayTitle: {
    fontFamily: "'Inter',sans-serif", fontSize: 28, fontWeight: 850, letterSpacing: '-0.03em',
    background: 'linear-gradient(135deg, #ffffff, #A78BFA)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  overlaySub: { fontSize: 20, fontWeight: 600 },
};

const miniS: Record<string, React.CSSProperties> = {
  wrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer' },
  info: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 },
};
